import type { BladPola } from '@/lib/api';

/**
 * BŁĘDY WALIDACJI Z SERWERA → POLA FORMULARZA.
 *
 * ⚠️ POWSTAŁO ZE ZGŁOSZENIA: „wyświetla się validation failed, ale nie wiadomo na jakim polu się
 * wyjebało […] Pole na którym coś nie działa powinno być oznaczone". Serwer podaje teraz nazwę
 * pola (`api/src/common/bledy-walidacji.ts`), ale samo to nic nie da — ktoś musi ją przełożyć na
 * `setError` konkretnego formularza, a nazwy z API nie zawsze mają odpowiednik na ekranie
 * (`machineIds` jest listą wyboru trzymaną poza `react-hook-form`, `groupId` w ogóle nie jest
 * polem). Dlatego rozdział na DWA wyniki, a nie ciche gubienie tego, co nie pasuje.
 *
 * ⚠️ TO, CO NIE MA SWOJEGO POLA, MUSI TRAFIĆ DO PASKA BŁĘDU. Przypięcie tylko rozpoznanych nazw
 * i wyrzucenie reszty dałoby najgorszy z możliwych stanów: okno bez ani jednego podświetlenia
 * i bez komunikatu, czyli „przycisk Zapisz nic nie robi".
 */
export type PodzialBledow = {
  /** Błędy, które da się podpiąć pod pole formularza. */
  podpiete: { pole: string; komunikat: string }[];
  /** Reszta — do pokazania w pasku błędu nad przyciskami. */
  reszta: BladPola[];
};

/**
 * Nazwa z API → nazwa pola formularza.
 *
 * Indeks tablicy odcinamy (`machineIds[0]` → `machineIds`), bo formularz ma pole dla CAŁEJ listy
 * albo nie ma go wcale; podświetlanie „elementu 0" nie istnieje jako pojęcie na ekranie.
 */
export const bezIndeksu = (pole: string): string => pole.replace(/\[\d+\]/g, '');

export function podzielBledy(pola: BladPola[], znanePola: readonly string[]): PodzialBledow {
  const znane = new Set(znanePola);
  const podpiete: PodzialBledow['podpiete'] = [];
  const reszta: BladPola[] = [];

  for (const b of pola) {
    const pole = bezIndeksu(b.pole);
    // ⚠️ PIERWSZY WYGRYWA. Jedno pole potrafi zebrać kilka zastrzeżeń, a `setError` i tak
    // pokazuje jedno — nadpisywanie zamieniałoby je w losowe.
    if (pole !== '' && znane.has(pole) && !podpiete.some((p) => p.pole === pole)) {
      podpiete.push({ pole, komunikat: b.komunikat });
    } else {
      reszta.push(b);
    }
  }

  return { podpiete, reszta };
}

/** Tekst do paska błędu: wiadomość serwera plus to, czego nie dało się przypiąć do pola. */
export function tekstPaska(wiadomosc: string, reszta: BladPola[]): string {
  if (reszta.length === 0) return wiadomosc;
  const opis = reszta.map((b) => (b.pole === '' ? b.komunikat : `${b.pole}: ${b.komunikat}`)).join('; ');
  return `${wiadomosc} (${opis})`;
}
